import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { mqUser } from 'utils/responsive';
import { FormInput } from 'ui/components/elements/FormInput/FormInput';
import { FormLabel } from 'ui/components/elements/FormLabel/FormLabel';
import { BankAccount } from './BankAccountManagement';

const ModalTitle = styled.h2`
    font-size: var(--font-large);
    font-weight: var(--font-bold);
    color: var(--main-font-color);
    margin: 0 0 10px 0;
`;

const Description = styled.p`
    font-size: var(--font-medium);
    color: #666;
    margin: 0 0 20px 0;
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    margin-bottom: 20px;
`;

const InfoSection = styled.div`
    margin-bottom: 30px;
    padding: 20px;
    background-color: #f9f9f9;
    border-radius: 8px;
`;

const InfoTitle = styled.h3`
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    color: var(--main-font-color);
    margin: 0 0 16px 0;
`;

const InfoRow = styled.div`
    display: flex;
    margin-bottom: 12px;
    &:last-child {
        margin-bottom: 0;
    }
`;

const InfoLabel = styled.span`
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    color: #666;
    min-width: 120px;
`;

const InfoValue = styled.span`
    font-size: var(--font-medium);
    color: var(--main-font-color);
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 30px;
`;

const Button = styled.button<{ primary?: boolean; disabled?: boolean }>`
    padding: 12px 24px;
    border: none;
    border-radius: 6px;
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.2s;
    opacity: ${(props) => (props.disabled ? 0.5 : 1)};

    ${(props) =>
        props.primary
            ? `
        background-color: var(--main-color-blue);
        color: var(--white);
        &:hover:not(:disabled) {
            background-color: #0056b3;
        }
    `
            : `
        background-color: #f5f5f5;
        color: var(--main-font-color);
        &:hover:not(:disabled) {
            background-color: #e0e0e0;
        }
    `}

    ${mqUser('SP')} {
        padding: 10px 16px;
        font-size: var(--font-small);
    }
`;

interface TransferApplyModalProps {
    transferId: number;
    amount: number;
    bankAccount: BankAccount | null;
    onClose: () => void;
    onApply: (accountNumber: string) => Promise<void>;
    loading?: boolean;
}

export const TransferApplyModal: React.FC<TransferApplyModalProps> = ({
    transferId,
    amount,
    bankAccount,
    onClose,
    onApply,
    loading = false,
}) => {
    const [accountNumber, setAccountNumber] = useState('');

    useEffect(() => {
        // モーダルが開かれたときに口座番号をリセット
        setAccountNumber('');
    }, [transferId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!accountNumber.trim()) {
            return;
        }
        try {
            await onApply(accountNumber);
            // 成功したらモーダルを閉じる
            onClose();
        } catch (error) {
            // エラーは親コンポーネントで処理される
            console.error('振込申請エラー:', error);
        }
    };

    if (!bankAccount) {
        return (
            <div>
                <ModalTitle>振込申請</ModalTitle>
                <Description>振込申請を行うには、先に入金口座情報を登録してください。</Description>
                <ButtonGroup>
                    <Button type="button" onClick={onClose}>
                        閉じる
                    </Button>
                </ButtonGroup>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit}>
            <ModalTitle>振込申請</ModalTitle>
            <Description>
                振込金額: ¥{Math.floor(amount).toLocaleString()}
                <br />
                口座情報を確認し、口座番号を入力してください。
            </Description>

            <InfoSection>
                <InfoTitle>登録済み口座情報</InfoTitle>
                <InfoRow>
                    <InfoLabel>銀行名:</InfoLabel>
                    <InfoValue>{bankAccount.bank_name || '-'}</InfoValue>
                </InfoRow>
                <InfoRow>
                    <InfoLabel>支店名:</InfoLabel>
                    <InfoValue>{bankAccount.branch_name || '-'}</InfoValue>
                </InfoRow>
                <InfoRow>
                    <InfoLabel>口座種別:</InfoLabel>
                    <InfoValue>{bankAccount.account_type_display || '-'}</InfoValue>
                </InfoRow>
                <InfoRow>
                    <InfoLabel>口座名義人カナ:</InfoLabel>
                    <InfoValue>{bankAccount.account_holder_kana || '-'}</InfoValue>
                </InfoRow>
            </InfoSection>

            <FormGroup>
                <FormLabel title="口座番号" isRequired />
                <FormInput
                    type="text"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    placeholder="例: 1234567"
                    required
                />
            </FormGroup>

            <ButtonGroup>
                <Button type="button" onClick={onClose} disabled={loading}>
                    キャンセル
                </Button>
                <Button type="submit" primary disabled={loading || !accountNumber.trim()}>
                    {loading ? '申請中...' : '申請する'}
                </Button>
            </ButtonGroup>
        </form>
    );
};
